import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { titlebarInset } from './desktop';

/**
 * The side drawer that tracks and albums open into.
 *
 * ## Why a drawer and not a navigation
 *
 * Clicking a track used to replace whatever you were doing - a feed you had
 * scrolled, a queue you were working, a conversation - with a track page, and
 * getting back meant the browser's back button and hoping the scroll survived.
 * For a site whose whole loop is *hear something, look at it, carry on*, that
 * is the most-travelled path in the product and it was the most disruptive.
 *
 * ## The URL still changes, and that is the point
 *
 * This is not a modal with local state. Opening one pushes `/tracks/123` with
 * the page you came from held in `location.state.background`, so the address
 * bar is shareable, the back button closes the drawer, and loading that same
 * URL cold renders the full page instead - see `AppRoutes`.
 *
 * ## It does not cover the dock
 *
 * Deliberately non-modal. A `<dialog showModal>` would put this in the top
 * layer and make the player unreachable, so somebody who opened a track could
 * not pause the one already playing. The scrim stops above the dock and the
 * panel does too.
 *
 * The cost is that focus is not trapped, which is the honest trade for a panel
 * that is not modal: focus moves in on open, Escape closes, and focus returns
 * to whatever opened it.
 */
export function Drawer({
  title,
  /** Where "open full page" goes - the same URL, without a background. */
  fullPath,
  /**
   * How much to dim the page behind.
   *
   * `full` for the ordinary case: a track or an album is a thing to look at,
   * and the page behind is context you have finished with for a moment.
   *
   * `light` for a drawer whose whole purpose is the page behind it - editing a
   * profile, where a 70% scrim over a blur makes judging a colour impossible.
   * The scrim is still there, because it is also what says "click here to
   * close", but it stops competing with the thing it is covering.
   */
  scrim = 'full',
  children,
}: {
  title: string;
  fullPath: string;
  scrim?: 'full' | 'light';
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const panel = useRef<HTMLDivElement>(null);

  /*
   * Back, rather than navigating to the background location.
   *
   * Opening pushed an entry, so closing has to pop it - otherwise the history
   * grows a step per track anybody glances at, and the back button turns into a
   * walk through everything they looked at rather than a way out of the page.
   */
  const close = () => navigate(-1);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate(-1);
    };

    document.addEventListener('keydown', onKey);

    /*
     * The page behind does not scroll while this is open.
     *
     * With a scrim over it, a wheel event moving the thing you cannot click
     * reads as a bug. The dock is outside this - it is fixed and above the
     * scrim - so playback stays reachable.
     *
     * `overflow: hidden` and nothing else. The scroll position survives it -
     * measured, because the alternative in every scroll-lock recipe is pinning
     * the body at a negative offset and restoring by hand, and that technique
     * breaks `position: sticky` inside the page for no benefit here.
     */
    const body = document.body;
    const previous = body.style.overflow;

    body.style.overflow = 'hidden';

    /*
     * `preventScroll`, because focus otherwise scrolls the document to reveal
     * its target. This one is fixed and already on screen, so there is nothing
     * to reveal and any movement would be the page lurching for no reason.
     */
    panel.current?.focus({ preventScroll: true });

    return () => {
      document.removeEventListener('keydown', onKey);
      body.style.overflow = previous;
      // Back where it came from, so closing does not dump a keyboard user at
      // the top of the document.
      opener?.focus?.();
    };
  }, [navigate]);

  return (
    <>
      {/* Stops above the dock, so the player stays clickable. */}
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close"
        data-drawer-scrim
        onClick={close}
        className={`fixed inset-x-0 top-0 z-40 cursor-default ${
          scrim === 'light' ? 'bg-scrim/30' : 'bg-scrim/70 backdrop-blur-[2px]'
        }`}
        style={{ bottom: 'var(--spacing-dock)', animation: 'gv-fade-in 160ms ease-out' }}
      />

      <div
        ref={panel}
        role="dialog"
        aria-label={title}
        tabIndex={-1}
        data-drawer
        className="fixed right-0 top-0 z-40 flex w-[min(560px,100vw)] flex-col border-l border-line bg-app outline-none"
        style={{ bottom: 'var(--spacing-dock)', animation: 'gv-slide-in 220ms cubic-bezier(0.22,1,0.36,1)' }}
      >
        {/*
          The right edge owes the window controls the same room the app header
          does.

          In the desktop shell the system draws minimise, maximise and close
          over the top of the page, and the drawer is pinned to `top-0
          right-0` - which is exactly where they are. Without this the "full
          page" link sits underneath the close button: still clickable in
          theory, invisible and unhittable in practice.

          `max()` rather than the inset alone, so the browser keeps its normal
          padding and only the shell pays for the controls.
        */}
        <header
          className="flex shrink-0 items-center gap-3 border-b border-line py-3.5 pl-5"
          style={{ paddingRight: `max(1.25rem, ${titlebarInset.right}px)` }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            data-drawer-close
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M10 3.5 5.5 8l4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <span className="min-w-0 flex-1 truncate text-sm font-medium text-soft">{title}</span>

          {/*
            No background state, so this renders the real page.
            The escape hatch for anything the drawer does not have room for, and
            the way somebody gets a URL they can obviously copy.
          */}
          <Link
            to={fullPath}
            data-drawer-full
            className="shrink-0 font-mono text-[11px] text-soft transition-colors hover:text-ink"
          >
            full page →
          </Link>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">{children}</div>
      </div>
    </>
  );
}
